import React, {
    type PropsWithChildren,
    createContext,
    useCallback,
    useContext,
    useEffect,
    useRef,
    useState
} from 'react';
import type {
    PromptResponse,
    Prompt,
    PromptRequest,
} from '../models/PromptTypes';
import type { IPromptService } from '../services/IPromptService';
import { useAuth } from '../../../contexts/auth/AuthContext';

interface Props {
    service: IPromptService;
}

interface IPromptContext {
    prompts: Prompt[];
    isLoading: boolean;
    getPromptsByKey: (key: string) => Promise<Prompt[]>;
    getAllPrompts: () => Promise<Prompt[]>;
    getPrompt: (key: string, promptKey: string) => Promise<Prompt>;
    createPrompt: (key: string, prompt: PromptRequest) => Promise<Prompt>;
    updatePrompt: (key: string, promptKey: string, prompt: PromptRequest) => Promise<PromptResponse<{}>>;
    deletePrompt: (key: string, promptKey: string) => Promise<boolean>;
    copyPrompt: (key: string, promptKey: string, prompt: PromptRequest) => Promise<PromptResponse<Prompt>>;
}

const defaultPromptContext: IPromptContext = {
    prompts: [],
    isLoading: false,
    getPromptsByKey: async () => { throw new Error('Function not implemented.'); },
    getAllPrompts: async () => { throw new Error('Function not implemented.'); },
    getPrompt: async () => { throw new Error('Function not implemented.'); },
    createPrompt: async () => { throw new Error('Function not implemented.'); },
    updatePrompt: async () => { throw new Error('Function not implemented.'); },
    deletePrompt: async () => { throw new Error('Function not implemented.'); },
    copyPrompt: async () => { throw new Error('Function not implemented.'); },
};

const PromptContext = createContext<IPromptContext>(defaultPromptContext);

const PromptProvider: React.FC<PropsWithChildren<Props>> = ({ service, children }) => {
    const [prompts, setPrompts] = useState<Prompt[]>([]);
    const [isLoading, setIsLoading] = useState<boolean>(true);

    const getPromptsByKey = useCallback(async (key: string) => {
        setIsLoading(true);
        const data = await service.getPromptsByKey(key);
        setIsLoading(false);
        return data;
    }, [service]);

    const getAllPrompts = useCallback(async () => {
        setIsLoading(true);
        const data = await service.getAllPrompts();
        setPrompts(data);
        setIsLoading(false);
        return data;
    }, [service]);

    const getPrompt = async (key: string, promptKey: string) => {
        return await service.getPrompt(key, promptKey);
    };

    const createPrompt = async (key: string, prompt: PromptRequest) => {
        const newPrompt = await service.createPrompt(key, prompt);
        setPrompts(prev => [...prev, newPrompt]);
        return newPrompt;
    };

    const updatePrompt = async (key: string, promptKey: string, prompt: PromptRequest) => {
        const result = await service.updatePrompt(key, promptKey, prompt);
        if (result.success && result.data) {
            setPrompts(prev =>
                prev.map(p => p.key === promptKey ? (result.data as Prompt) : p)
            );
        }
        return result;
    };

    const deletePrompt = async (key: string, promptKey: string) => {
        const result = await service.deletePrompt(key, promptKey);
        if (result) {
            setPrompts(prev => prev.filter(p => p.key !== promptKey));
        }
        return result;
    };

    const copyPrompt = async (key: string, promptKey: string, prompt: PromptRequest) => {
        const result = await service.copyPrompt(key, promptKey, prompt);
        if (result.success && result.data) {
            setPrompts(prev => [...prev, result.data]);
        }
        return result;
    };

    const { isLoggingIn, isStartingUp, isLoggingOut } = useAuth();

    const didInitialize = useRef(false);

    useEffect(() => {
        if (didInitialize.current) return;

        if (isLoggingIn || isStartingUp || isLoggingOut)
            return;

        (async () => {
            didInitialize.current = true;
            try {
                await getAllPrompts();
            } catch (error) {
                console.error('Failed to load prompts:', error);
                setIsLoading(false);
            }
        })();
    }, [getAllPrompts, isLoggingIn, isLoggingOut, isStartingUp]);

    return (
        <PromptContext.Provider value={{
            prompts,
            isLoading,
            getPromptsByKey,
            getAllPrompts,
            getPrompt,
            createPrompt,
            updatePrompt,
            deletePrompt,
            copyPrompt,
        }}>
            {children}
        </PromptContext.Provider>
    );
};

export const usePrompt = () => useContext(PromptContext);

export default PromptProvider;
